/**
 * voice-v8 — Auth-API-Client.
 *
 * Wrappt die /api/auth/{me,request,logout}-Endpoints in eine schmale
 * TypeScript-API. Cookie-basierte Session — Browser sendet das Cookie
 * automatisch mit, wir müssen nichts manuell tracken.
 */

interface MeResponseAnonymous {
  authenticated: false;
  /** Wenn gesetzt: Auth ist deaktiviert, System läuft als Single-User. */
  defaultUserId?: number;
}

interface MeResponseAuthenticated {
  authenticated: true;
  user: { id: number; email: string; name: string | null; picture?: string | null };
}

export type MeResponse = MeResponseAnonymous | MeResponseAuthenticated;

export async function fetchMe(): Promise<MeResponse> {
  const res = await fetch("/api/auth/me", { credentials: "same-origin" });
  if (!res.ok) {
    throw new Error(`/api/auth/me failed: ${res.status}`);
  }
  return (await res.json()) as MeResponse;
}

interface RequestMagicLinkResult {
  ok: boolean;
  error?: string;
  detail?: string;
}

export async function requestMagicLink(
  email: string,
  name?: string,
): Promise<RequestMagicLinkResult> {
  const res = await fetch("/api/auth/request", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "same-origin",
    body: JSON.stringify({ email, name }),
  });
  const data = (await res.json().catch(() => ({}))) as RequestMagicLinkResult;
  if (!res.ok) {
    return {
      ok: false,
      error: data.error ?? `http-${res.status}`,
      detail: data.detail,
    };
  }
  return { ok: true };
}

export async function logout(): Promise<void> {
  await fetch("/api/auth/logout", {
    method: "POST",
    credentials: "same-origin",
  });
}

interface LoginResult {
  ok: boolean;
  error?: string;
  user?: { id: number; email: string; name: string | null };
}

/**
 * Der Anmelde-Schutz dieses Browsers. (28.09.2026, Ticket 2713 V2)
 *
 * Der Server legt dafür einen Keks an und gibt dessen Prüfsumme zurück
 * (server/anmelde-schutz.ts). Die Anmeldung mit Passwort schickt sie im Kopf
 * mit, der Google-Start der App im Link — so kann keine fremde Seite eine
 * Anmeldung auslösen. null, wenn der Server gerade nicht antwortet.
 */
export async function holeAnmeldeSchutz(): Promise<string | null> {
  try {
    const res = await fetch("/api/auth/schutz", {
      credentials: "same-origin",
      cache: "no-store",
    });
    if (!res.ok) return null;
    const d = (await res.json()) as { schutz?: unknown };
    return typeof d.schutz === "string" ? d.schutz : null;
  } catch {
    return null;
  }
}

export async function loginWithPassword(
  email: string,
  password: string,
  schutz: string | null,
): Promise<LoginResult> {
  const res = await fetch("/api/auth/login", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(schutz ? { "X-Anmelde-Schutz": schutz } : {}),
    },
    credentials: "same-origin",
    body: JSON.stringify({ email, password }),
  });
  const data = (await res.json().catch(() => ({}))) as LoginResult;
  if (!res.ok) {
    return { ok: false, error: data.error ?? `http-${res.status}` };
  }
  return { ok: true, user: data.user };
}
